import { IDate } from '@/coreDomainsModule/date';
import { openFailToAcquireModal } from '@/corePagesModule/components/modal/modalActions';
import { defineStore } from 'pinia';
import { h, reactive } from 'vue';
import { CalendarOrder } from '../domains/entities/CalendarOrder';
import { CalendarOrderService } from '../domains/services/CalendarOrderService';
import { BulkOrderInfo, BulkOrderInfoType } from '../domains/valueObjects/BulkOrderInfo';
import { FilterType } from '@/coreDomainsModule/entities/filter';
import { IOrderRepository } from '@/coreInfrastructureModule/interfaces/IOrderRepository';
import { GetOrderUseCase } from '../useCases/GetOrderUseCase';
import { UpdateOrderUseCase } from '../useCases/UpdateOrderUseCase';
import { FilterContentItem } from '@/corePagesModule/filterModal/filterModal';
import { DateService } from '@/coreDomainsModule/services/dateService';
import { OrderStatus } from '@/coreDomainsModule/entities/arrival';
import { convertUnixTimeToIDate } from '@/corePagesModule/BaseTableContent/utils/dateFormat';
import { AlertType } from '@/coreDomainsModule/entities/hasRecommendOrders';
import { IOrderGroup } from '@/coreDomainsModule/entities/IOrderGroup';
import { diContainer } from '@/coreContainer/container';
import { showInfoModal } from '@/corePagesModule/components/InfoModal/InfoModalManager';
import { i18n } from '@/i18n';
import OrderGroupBulkChangeETAModalContent from '@/corePagesModule/components/OrderGroup/OrderGroupBulkChangeETAModalContent.vue';
import { OrderGroupFactory } from '@/coreDomainsModule/factories/OrderGroupFactory';
import { waitLoading } from '@/corePagesModule/components/InfoModal/waitLoading';
import { simpleCopy } from '@/utils/deepCopy';
import { useTableFilter } from '@/corePagesModule/composables/useTableFilter';

const t = i18n.t;

export const useOrderCalendarStore = defineStore('orderCalendar', () => {
  const state: IOrderCalendarState = reactive({
    currentMonthDate: new Date(),
    draggingOrder: undefined,
    dateOpeningOtherOrderModal: undefined,
    allOrders: [],
    selectedInfo: {
      warehouseName: [],
      dailyDeliveryLimit: 0,
      supplierName: [],
      bulkOrder: { amount: 0, type: 'min' },
      businessName: [],
      packageAmount: [],
      groupId: [],
    },
    filterInfo: {
      isOpenFilterType: undefined,
    },
    modalInfo: {
      isOpen: false,
      day: undefined,
    },
    inventorySimulationResult: [],
    orderAlertMap: {},
    orderGroupList: [],
  });

  // フィルター情報のComposable
  const { cachedFilterItems, bulkUpdateFilterInfos, getFilterItemsValue, initTableFilter } = useTableFilter('orderCalendar');

  /**
   * リセット関数
   */
  const $reset = () => {
    state.draggingOrder = undefined;
    state.dateOpeningOtherOrderModal = undefined;
    state.allOrders = [];
    state.selectedInfo = {
      warehouseName: [],
      dailyDeliveryLimit: 0,
      bulkOrder: { amount: 0, type: 'min' },
      groupId: [],
    };
    state.filterInfo = {
      isOpenFilterType: undefined,
    };
    state.modalInfo = {
      isOpen: false,
      day: undefined,
    };
    state.orderGroupList = [];
  };

  /**
   * 発注情報を取得する
   * @param orderRepository - 発注情報のリポジトリ
   */
  const fetchOrders = async (orderRepository: IOrderRepository): Promise<void> => {
    await initTableFilter();
    await fetchOrderGroup();
    const allFilterItems: FilterContentItem[] = [
      { filterType: 'WAREHOUSE', selectedValue: state.selectedInfo.warehouseName, label: '' },
      { filterType: 'GROUP_ID', selectedValue: state.selectedInfo.groupId, label: '' },
      ...cachedFilterItems.value,
    ];
    // 空文字のみが入ってるフィルターは除外する
    const useFilterItems = allFilterItems.filter(
      (item) => item.selectedValue.length > 0 && item.selectedValue.some((value) => value !== '')
    );
    const result = await GetOrderUseCase.execute(
      orderRepository,
      undefined,
      undefined,
      undefined,
      useFilterItems,
      state.currentMonthDate,
      true
    );

    switch (result.status.type) {
      case 'success':
        const orderData = result.status.data;
        // 発注情報をstateにセット
        state.allOrders = orderData;

        // 発注情報のアラートを作成
        _createOrderAlert(orderData);
        break;
      case 'error':
        openFailToAcquireModal();
        break;
    }
  };

  /**
   * 発注情報を保存する
   * @param orderRepository - 発注情報のリポジトリ
   * @param orders - 発注情報
   */
  const saveOrders = async (
    orderRepository: IOrderRepository,
    orders?: CalendarOrder[]
  ): Promise<ReturnType<typeof UpdateOrderUseCase.execute>> => {
    return await UpdateOrderUseCase.execute(orderRepository, orders || state.allOrders);
  };

  /**
   * ローカルの発注情報を更新する
   * @param changedOrders - 変更された発注情報
   */
  const updateStateOrders = (changedOrders: CalendarOrder[]) => {
    // ローカルに反映する
    const localData = state.modalInfo.day !== undefined ? getOrdersByDate(state.modalInfo.day) : undefined;
    changedOrders?.forEach((order) => {
      const targetOrder = localData?.find((o) => o.arrivalId === order.arrivalId);
      if (targetOrder) {
        targetOrder.amount = order.amount;
        targetOrder.status = order.status;
      }
    });
  };

  /**
   * ドラッグ時に発注情報を保持する
   * @param order - 発注
   */
  const setDraggingOrder = (order: CalendarOrder) => {
    state.draggingOrder = order;
  };

  /**
   * ドラッグドロップ時に発注情報の日付を変更する
   * @param date - 日付
   * @param orderRepository - 発注情報のリポジトリ
   */
  const moveDraggingOrder = async (date: IDate, orderRepository: IOrderRepository) => {
    if (state.draggingOrder === undefined) return;
    const isSameDate =
      state.draggingOrder.crossDate === undefined ? false : DateService.equalYearMonthDayInfo(state.draggingOrder.crossDate, date);
    state.draggingOrder.crossDate = date;
    const targetOrders = [state.draggingOrder];
    state.draggingOrder = undefined;

    if (isSameDate) return;
    // 納期に変更があればチェック→DBに保存
    // 発注グループに所属している場合、納期変更の修正を出す
    if (targetOrders[0].orderGroupId) {
      await _checkAndUpdateOrderGroupCrossDate(targetOrders[0].orderGroupId, DateService.convertIDateToUnixTime(date));
    } else {
      await saveOrders(orderRepository, targetOrders);
    }
    await fetchOrders(orderRepository);
    return;
  };

  /**
   * 特定の日付の発注情報を取得する
   * Return時にフィルターもかける
   * @param date - 日付
   */
  const getOrdersByDate = (date: IDate): CalendarOrder[] => {
    return CalendarOrderService.getOrdersByDate(state.allOrders, date);
  };

  /**
   * １日の納入上限数量内に収まっているかどうか
   * @param date - 日付
   */
  const isValidDailyDeliveryLimit = (date: IDate) => {
    const orders = getOrdersByDate(date);
    if (orders.length === 0) return true;
    if (state.selectedInfo.dailyDeliveryLimit <= 0) return true;
    if (state.selectedInfo.warehouseName.length === 0) return true;
    return getOrdersByDate(date).reduce((acc, order) => acc + order.amount, 0) <= state.selectedInfo.dailyDeliveryLimit;
  };

  /**
   * まとめ発注ルールに適合しているかどうか
   * @param date - 日付
   */
  const isValidBulkOrderRules = (date: IDate) => {
    const orders = getOrdersByDate(date);
    if (state.selectedInfo.bulkOrder === undefined || orders.length === 0) return true;
    const sumAmount = orders.reduce((acc, order) => acc + order.amount, 0);
    switch (state.selectedInfo.bulkOrder.type) {
      case BulkOrderInfoType.MIN:
        return sumAmount >= state.selectedInfo.bulkOrder!.amount;
      case BulkOrderInfoType.MAX:
        return sumAmount <= state.selectedInfo.bulkOrder!.amount;
      case BulkOrderInfoType.IS:
        return sumAmount === state.selectedInfo.bulkOrder!.amount;
    }
  };

  /**
   * モーダル情報をセットする
   * @param isOpen - モーダルが開いているかどうか
   * @param day - 日付
   */
  const setModalInfo = (isOpen: boolean, day?: IDate) => {
    state.modalInfo.isOpen = isOpen;
    state.modalInfo.day = day;
  };

  /**
   * 納期を確定する
   * @param order - 確定させる発注情報
   * @param orderRepository - 発注情報のリポジトリ
   */
  const fixDeliveryDate = (order: CalendarOrder, orderRepository: IOrderRepository) => {
    if (!CalendarOrderService.isChangeableCrossDate(order)) return;
    if (order.status === undefined) return;
    order.status = CalendarOrderService.getProgressOrderStatusWhenFixDeliveryDate(order.status);
    saveOrders(orderRepository, [order]);
  };

  /**
   * グループが選択された際の処理
   * @param orderRepository - 発注情報のリポジトリ
   * @param selectedGroupIds - 選択されたグループのID
   */
  const onSelectedGroup = async (orderRepository: IOrderRepository, selectedGroupIds: string[]) => {
    state.selectedInfo.groupId = selectedGroupIds ?? '';
    if (selectedGroupIds.length === 0) {
      // まとめ発注情報を初期化
      _initBalkOrderInfo();
    }
    // api叩く
    await fetchOrders(orderRepository);
  };

  /**
   * 発注グループを取得
   */
  const fetchOrderGroup = async () => {
    const result = await diContainer.getOrderGroupUseCase.execute();
    if (result.status.type === 'success') {
      state.orderGroupList = result.status.data;
    }
  };

  /**
   * フィルターモーダルの適用ボタンクリック時の処理
   * @param orderRepository - 発注情報のリポジトリ
   * @param filterItems - フィルターモーダル内で選択された項目
   */
  const onAppliedFilterModal = async (orderRepository: IOrderRepository, filterItems: FilterContentItem[]) => {
    // フィルターモーダルの選択情報を更新
    await bulkUpdateFilterInfos(simpleCopy(filterItems));
    await fetchOrders(orderRepository);
  };

  // private

  /**
   * まとめ発注情報を初期化する
   * @param bulkOrder - まとめ発注情報
   */
  const _initBalkOrderInfo = (bulkOrder?: BulkOrderInfo) => {
    const defaultBulkOrder: BulkOrderInfo = { amount: 0, type: 'min' };
    state.selectedInfo.bulkOrder = bulkOrder ?? defaultBulkOrder;
  };

  /**
   * 発注のアラートを作成する
   * @param order - 発注情報
   */
  const _createOrderAlert = (order: CalendarOrder[]): void => {
    // 作成前に全てのアラートを初期化
    state.orderAlertMap = {};

    for (const orderItem of order) {
      if (orderItem.crossDate === undefined) continue;
      const orderAlert: AlertType[] = [];
      // 納期遅延しているかどうか（警告）
      const today: IDate = convertUnixTimeToIDate(new Date().getTime());
      const orderStatus = orderItem.status;
      const estimatedTimeOfArrivalDate: IDate = orderItem.crossDate; // 納期
      // 納期の翌日になっても納入済ステータスまたは表示終了ステータス以外の場合
      if (
        !(orderStatus === OrderStatus.Delivered || orderStatus === OrderStatus.DisplayEnded || orderStatus === OrderStatus.Unknown) &&
        DateService.compareDates(today, estimatedTimeOfArrivalDate) > 0
      ) {
        orderAlert.push('DeliveryDateWarning');
      }

      // 納入済ステータスまたは表示終了ステータス以外 & 在庫0予想があるかどうか（警告）
      if (
        !(orderStatus === OrderStatus.Delivered || orderStatus === OrderStatus.DisplayEnded || orderStatus === OrderStatus.Unknown) &&
        orderItem.isOutOfStockExpected
      ) {
        orderAlert.push('OrderAmountWarning');
      }

      // 納入済ステータスまたは表示終了ステータス以外 &納期当日になった場合（注意喚起）
      if (
        !(orderStatus === OrderStatus.Delivered || orderStatus === OrderStatus.DisplayEnded || orderStatus === OrderStatus.Unknown) &&
        DateService.compareDates(today, estimatedTimeOfArrivalDate) === 0
      ) {
        orderAlert.push('DeliveryDateInfo');
      }

      // 納入済ステータスまたは表示終了ステータス以外 & 発注出力から1週間経っても納期回答が来ていない場合（注意喚起）
      const outputDate = orderItem.outputDate;
      if (outputDate != undefined) {
        const oneWeekLater: IDate = DateService.getShiftedIYearMonthByDays(outputDate.year, outputDate.month, outputDate.day, 7);
        if (
          !(orderStatus === OrderStatus.Delivered || orderStatus === OrderStatus.DisplayEnded || orderStatus === OrderStatus.Unknown) &&
          DateService.compareDates(today, oneWeekLater) >= 0
        ) {
          orderAlert.push('DeliveryDateConfirmedInfo');
        }
      }

      state.orderAlertMap[orderItem.arrivalId] = orderAlert;
    }
  };

  /**
   * 納期更新時発注グループの納期とあっているかをチェックし、場合に応じた処理を行う
   * @param targetOrderGroupId - 対象の発注グループId
   * @param updatedDate - 更新する日付(UnixTime)
   */
  const _checkAndUpdateOrderGroupCrossDate = async (targetOrderGroupId: string, updatedDate: number) => {
    try {
      const linkedOrderGroup = state.orderGroupList.find((group) => group.orderGroupId === targetOrderGroupId);
      if (!linkedOrderGroup) {
        throw new Error();
      }
      if (linkedOrderGroup.eta === updatedDate) {
        // 納期変えている時点でありえないけど一応
        return;
      }
      const isConfirm = await showInfoModal({
        modalTitle: t('発注グループの納期を一括変更'),
        modalDescription: t(
          '選択した発注はグループ化されています。グループの納期も一括変更しますか？個別に納期を変更したい場合はグループから取り外してから納期変更してください。'
        ),
        modalContent: h(OrderGroupBulkChangeETAModalContent, {
          changedETA: updatedDate,
          targetOrderGroup: linkedOrderGroup,
        }),
        backButtonLabel: t('キャンセル'),
        nextButtonLabel: t('納期一括変更'),
        enableOutsideClick: false,
        modalWidth: 1024,
      });
      if (!isConfirm) {
        return;
      }
      const updateOrderGroupResult = await waitLoading(
        diContainer.updateOrderGroupUseCase.execute(
          linkedOrderGroup.orderGroupId,
          OrderGroupFactory.create({ ...linkedOrderGroup, eta: updatedDate })
        )
      );
      if (updateOrderGroupResult?.status.type !== 'success') {
        throw new Error();
      }
      return;
    } catch (e) {
      await showInfoModal({
        modalTitle: t('エラー'),
        modalDescription: t('処理中にエラーが発生しました。時間をおいて再度お試しください。'),
        backButtonLabel: 'OK',
        icon: 'error',
        colorStyle: 'error',
        enableOutsideClick: false,
        isDisabledNextButton: true,
      });
      $reset();
    }
  };

  return {
    state,
    $reset,
    fetchOrders,
    saveOrders,
    getOrdersByDate,
    setDraggingOrder,
    moveDraggingOrder,
    isValidDailyDeliveryLimit,
    isValidBulkOrderRules,
    setModalInfo,
    fixDeliveryDate,
    onSelectedGroup,
    updateStateOrders,
    onAppliedFilterModal,
    getFilterItemsValue,
  };
});

/**
 * 発注カレンダー画面のStoreを管理する
 */
interface IOrderCalendarState {
  /**
   * 現在の年月
   */
  currentMonthDate: Date;
  /**
   * ドラッグ中の発注Id
   */
  draggingOrder?: CalendarOrder;
  /**
   * 他N件をクリックしたときに開くモーダルを現在開いているIDate（排他制御のためのプロパティ）
   */
  dateOpeningOtherOrderModal?: IDate;
  /**
   * 発注情報一覧
   */
  allOrders: CalendarOrder[];
  /**
   * 選択中の情報
   */
  selectedInfo: {
    // 置き場名・１日の納入上限数量・仕入れ先・まとめ発注・商品・容量
    /**
     * 置き場名
     */
    warehouseName: string[];
    /**
     * １日の納入上限数量
     */
    dailyDeliveryLimit: number;
    /**
     * まとめ発注
     */
    bulkOrder: BulkOrderInfo;
    /**
     * グループID
     */
    groupId: string[];
  };
  /**
   * フィルター情報
   */
  filterInfo: {
    /**
     * 選択中のフィルター種別（排他制御のためのプロパティ）
     */
    isOpenFilterType?: FilterType;
  };
  /**
   * モーダル情報
   */
  modalInfo: {
    /**
     * モーダルが開いているかどうか
     */
    isOpen: boolean;
    /**
     * 日付
     */
    day?: IDate;
  };
  orderAlertMap: {
    [key: string]: AlertType[];
  };
  /**
   * 在庫シミュレーション結果
   */
  inventorySimulationResult: number[];

  orderGroupList: IOrderGroup[];
}
